import React from 'react';
import Field from '../../Field';
import { Radio } from '../../FormControls';
import { coreSync } from '../../../lib/core';
import { durationError, DURATION_MAX_MINUTES } from '../../../domain/eventSeries';
import s from './addStudent.module.css';
import { DecimalInput } from '../../RegionInputs';

// Step 3. These are defaults for the student, not a schedule — the lessons
// themselves are added on the calendar.
//
// Category is a pick from the managed list (#110), as MMS's Step 3 has a
// Category select beside the Duration number field (SPEC.md §1.2): a typed
// string would sit outside every rename, which matches on `categoryId`. Keyed
// on the category's id (#128).

const RATE_HINT = {
  hourly: 'Per hour',
  fixed_monthly: 'Per month',
};

export default function LessonSettingsStep({
  form, set, categories = [], onCategoryChange, rateError = null,
}) {
  return (
    <>
      <h3 className={s.stepTitle}>Lesson Settings</h3>
      <div className={s.stepLead}>What does the typical lesson look like for this student?</div>

      <div className={s.pairTight}>
        {/* `htmlFor`/`id`: a select whose label is not associated with it is
            announced as an unlabelled combobox, and its options mean nothing
            without its name. */}
        <Field label="Category" htmlFor="add-student-category" hint="Manage the list from Calendar → Options">
          <select
            id="add-student-category"
            className={s.input}
            value={form.categoryId}
            onChange={(e) => onCategoryChange?.(e.target.value)}
          >
            {/* Only while the list has not arrived — a frame at most, since
                `useCategories` falls back to the built-in six synchronously. An
                empty select with no placeholder would look like a category
                called nothing. */}
            {form.categoryId ? null : <option value="">Loading…</option>}
            {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </Field>
        {/* As on the event drawer (#137): `step="5"` is advisory on an input
            nothing submits, so whole minutes in 1..1440 are refused at the
            field, matching the rule `requiredIntInRange(d,
            'defaultDurationMin', 1, 1440)` (#149, #352). `canAdvance` in
            hooks/useAddStudentForm.js holds Next shut while this message is
            up, and `save()` re-checks, because Save is pressed on the step
            after this one.

            Required (#352): MMS pre-fills 30 and refuses an empty box on Save
            ("Please enter a valid duration"), and so does this (`emptyForm` in
            hooks/useAddStudentForm.js). A make-up credit is divided by this
            number, so a student with no duration would have a balance nothing
            could express. */}
        <Field
          label="Duration" hint="Minutes"
          error={durationError(form.durationMin, DURATION_MAX_MINUTES, { required: true })}
        >
          <input
            type="number" min="5" step="5" className={s.input}
            value={form.durationMin} onChange={(e) => set('durationMin', e.target.value)}
          />
        </Field>
      </div>

      <div className={s.groupLabelSpaced}>Lesson Price</div>
      {/* From the core (#214), as in the edit drawer. */}
      {coreSync().pricingModes().map((m) => (
        <Radio
          key={m.id} name="pmode" label={m.label} help={m.help}
          checked={form.pricingMode === m.id}
          onChange={() => set('pricingMode', m.id)}
        />
      ))}

      {form.pricingMode !== 'none' ? (
        <div className={[s.spaced, s.narrow].join(' ')}>
          {/* As Duration above (#164): a rate past firestore.rules'
              `optionalMinor` is refused here. `rateError` is computed in
              useAddStudentForm.js, which has the currency the figure is typed
              in. */}
          <Field
            label="Rate" hint={RATE_HINT[form.pricingMode] || 'Per lesson'}
            error={rateError}
          >
            {/* The box is the Region's (`DecimalInput`, #526): `16,95` in
                Germany. A third decimal is truncated one level up, by `set` in
                src/hooks/useAddStudentForm.js. */}
            <DecimalInput
              className={s.input}
              value={form.rate} onChange={(e) => set('rate', e.target.value)}
            />
          </Field>
        </div>
      ) : null}
    </>
  );
}
